import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {FieldType} from "@ngx-formly/core";
import {
  DocScanWebComponentImageResult,
  DocScanWebComponentResult,
  DocumentScanningStatus
} from "../../models/document-scanning.models";
import {select, Store} from "@ngrx/store";
import {ActivatedRoute} from "@angular/router";
import {Observable, Subscription} from "rxjs";
import {Clipboard} from '@angular/cdk/clipboard';
import {SharedConfigService} from '../../services/shared-config.service';
import {DocumentScanningSignalRService} from '../../services/document-scanning-signal-r.service';
import {SharedState} from '../../+state/shared.reducer';
import {getDocumentScanningError, getDocumentScanningStatus, getQrCodeValue} from '../../+state/shared.selectors';
import {
  getShortUrl,
  resetAutoFillForm,
  scanDocumentUsingWebService,
  skipDocumentScanning
} from '../../+state/shared.actions';
import {DeviceDetectorService} from '../../services/device-detector.service';
import {AppModeDetectorService} from "../../services/app-mode-detector.service";


@Component({
  selector: 'formly-document-scanning-device-detector-field',
  template: `

    <div *ngIf="isMobileDevice else desktopDevice">
      <!-- todo: set mobile device view -->
      <p>is mobile {{isMobileDevice}}</p>
    </div>

    <ng-template #desktopDevice>
      <div [ngSwitch]="(documentScanningStatus$|async)">
        <div *ngSwitchCase="scanningStatuses.InProgress">
          <ds-container>
            <ds-row>
              <ds-col span="2">
                <ds-icon name="content-loading" spinner size="48"></ds-icon>
              </ds-col>
              <ds-col span="10">
                <h5>Scanning document...</h5>
                <p>
                  You'll be automatically redirected when the document scanning complete.<br/>
                  Please do not close this window.
                </p>
              </ds-col>
            </ds-row>
          </ds-container>
        </div>
        <div *ngSwitchCase="scanningStatuses.Success">
          <ds-container>
            <ds-row>
              <ds-col span="2">
                <ds-icon name="content-done" color="feedback-success-dark" size="48"></ds-icon>
              </ds-col>
              <ds-col span="10">
                <h5 class="text-feedback-success-dark">Success</h5>
                <p>
                  <b>Redirecting...</b> <br/><br/>
                  Check your data in the next sections. Correct them in case of errors.
                </p>
              </ds-col>
            </ds-row>
          </ds-container>
        </div>
        <div *ngSwitchCase="scanningStatuses.Error">
          <ds-container>
            <ds-row>
              <ds-col span="2">
                <ds-icon name="content-warning" color="feedback-error-dark" size="48"></ds-icon>
              </ds-col>
              <ds-col span="10">
                <h5 class="text-feedback-error-dark">An error occurred scanning your document</h5>
                <ul>
                  <li *ngFor="let error of errors">
                    <p class="text-feedback-error-dark">{{error}}</p>
                  </li>
                </ul>
                <p class="mt-40">
                  <a style="cursor:pointer; text-decoration: underline" class="text-link" (click)="retry()">
                    Retry
                  </a>
                  or
                  <a style="cursor:pointer; text-decoration: underline" class="text-link" (click)="skip()">
                    click next to fill the form manually
                  </a>
                </p>
              </ds-col>
            </ds-row>
          </ds-container>
        </div>
        <div *ngSwitchDefault>
          <div class="border px-16 py-16 pb-24 pt-24 mb-40" style="border-radius: 4px">
            <ds-row>
              <ds-col span="8">
                <h5>Scan the QR code by using your mobile phone</h5>
                <span class="small text-light mt-4">In case your camera or your mobile phone does not have a QR code reader
            installed, you’ll need to download an application for this purpose.</span>
              </ds-col>
              <ds-col span="4">
                <qrcode
                  [qrdata]="qrCodeValue$ | async"
                  allowEmptyString="true"
                  [width]="200"
                  (click)="copyQrCodeValueToClipboard()"
                >
                </qrcode>
              </ds-col>
            </ds-row>
          </div>
          <ds-file-selector
            orientation="landscape" #fileSelectorRef
            accept=".jpg,.jpeg,.png,.pdf"
            multiple
            (dsChange)="onBrowseFilesChange($event)"
            class="mt-40"
          >
            <div slot="heading">
              <h5>Upload document photos from your computer</h5>
              <span
                [ngClass]="{
                'text-feedback-error-dark': (validFiles > 0 && validFiles > 2) || invalidFiles != 0,
                'text-light': validFiles == 0 && invalidFiles == 0,
                'text-feedback-success-dark': validFiles >= 1 && validFiles <= 2 && invalidFiles == 0
              }"
                class="small mt-4">
                Upload both sides, each side in its own file. Full colour, cropped and straightened. <br/>
                Allowed photo formats: jpeg, pdf or png. <br/>
                Max. file size (photo) – 2MB.
            </span>
              <span *ngIf="(validFiles != 0 && validFiles > 2) || invalidFiles != 0"
                    class="small text-feedback-error-dark mt-4">
                You must upload 1 or 2 valid files
            </span>
            </div>
            <div slot="browse">
              <ds-button
                prefix-icon="action-upload"
                (dsButtonClick)="browseFiles()"
              >
                Upload files
              </ds-button>
            </div>
            <ng-container *ngIf="files.length > 0">
              <div slot="preview">
                <h5 class="mb-16">Files to scan</h5>
                <ng-container *ngFor="let file of files; index as i">
                  <ds-file-preview
                    [name]="file.name"
                    invalidFile [file]="file"
                    (dsRemove)="onFileRemove(i)"
                  ></ds-file-preview>
                </ng-container>
              </div>
              <div slot="actions">
                <ds-button appearance="secondary" (dsButtonClick)="handleRemoveAllFiles()">Remove files</ds-button>
                <ds-button
                  appearance="highlight"
                  [disabled]="validFiles < 1 || validFiles > 2 || invalidFiles != 0"
                  (click)="confirmAndUpload()"
                >
                  Confirm
                </ds-button>
              </div>
            </ng-container>
          </ds-file-selector>
          <p class="mt-40">
            Do you have issues with the document scanning process?
            <a style="cursor:pointer; text-decoration: underline" class="text-link" (click)="skip()">
              Fill your personal details form manually.
            </a>
          </p>
        </div>
      </div>
    </ng-template>

  `,
  styles: [],
})
export class FormlyDocumentScanningDeviceDetectorFieldComponent extends FieldType
  implements OnInit, AfterViewInit, OnDestroy, AfterViewChecked {

  @Output() processFinished = new EventEmitter<DocScanWebComponentResult>();

  @ViewChild('fileSelectorRef', {static: false}) fileSelectorRef: ElementRef<HTMLDsFileSelectorElement>;

  regulaLicense: string;
  regulaResult: DocScanWebComponentResult = {text: [], images: []};
  documentScanningStatus$: Observable<DocumentScanningStatus>;
  scanningStatuses = DocumentScanningStatus;
  qrCodeValue$: Observable<string>;
  private fileSelectorElem: HTMLDsFileSelectorElement;
  files: File[] = [];
  validFiles = 0;
  invalidFiles = 0;
  private formCode: string;
  isMobileDevice: boolean;
  acceptedFileTypes: string[] = ['image/jpg', 'image/jpeg', 'image/png', 'image/pdf', 'application/pdf'];
  private subs = new Subscription();
  private qrCodeValue = '';
  errors: string[];
  private isBackoffice: boolean;

  constructor(
    private configService: SharedConfigService,
    private docScanningSignalRService: DocumentScanningSignalRService,
    private sharedStore: Store<{ shared: SharedState }>,
    private route: ActivatedRoute,
    private deviceDetector: DeviceDetectorService,
    private clipboard: Clipboard,
    private appDetector: AppModeDetectorService
  ) {

    super();

    this.configService.getConfig().then((config) => {
      this.regulaLicense = config.regulaLicense;
    });

    this.docScanningSignalRService.signalRConnected
      .subscribe((connected) => {
        console.log('is signalr connected', connected);
      });
  }

  // This hook is triggered after the view and child views are checked for changes
  // Without use it the fileSelectorRef template reference variable will be undefined
  // in case of an error scanning the document the first time and retrying uploading the files
  ngAfterViewChecked(): void {
    // The template reference variable is available here
    if (!this.isMobileDevice) {
      if (this.fileSelectorRef?.nativeElement) {
        this.fileSelectorElem = this.fileSelectorRef?.nativeElement;
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async ngAfterViewInit() {
    if (!this.isMobileDevice) {
      await customElements.whenDefined('ds-file-selector');
      await this.fileSelectorRef?.nativeElement.componentOnReady();

      this.fileSelectorElem = this.fileSelectorRef?.nativeElement;
    }
  }

  ngOnInit(): void {
    this.documentScanningStatus$ = this.sharedStore.pipe(select(getDocumentScanningStatus));
    this.qrCodeValue$ = this.sharedStore.pipe(select(getQrCodeValue));

    this.isMobileDevice = this.deviceDetector.isMobileDevice();

    // The url must contain the user identifier (uid) as a link between the mobile and desktop clients.
    // In this way signal r can notify all the clients of the same user (same uid, so same signalr group)
    // about the state of the document scanning process.
    const uid = sessionStorage.getItem('uid');
    this.isBackoffice = this.appDetector.isBackoffice();

    // NOTE: if is backoffice we don't have the formCode but the formTemplateId
    if (this.isBackoffice) {
      const url = window.location.href;
      const parts = url.split('/form-templates/');
      if (parts.length >= 2) {
        this.formCode = parts[1].split('/')[0];
      }
    } else {
      this.formCode = this.route.snapshot.paramMap.get('id');
    }

    const longUrl = `${window.location.origin}/mobile-document-scanning?formCode=${this.formCode}&uid=${uid}&isBackoffice=${this.isBackoffice}`;

    this.sharedStore.dispatch(getShortUrl({longUrl: longUrl}));

    // This is useful to copy the qr code value to the clipboard, when user click on it
    this.subs.add(this.qrCodeValue$.subscribe((x) => this.qrCodeValue = x));
    this.subs.add(this.sharedStore.pipe(select(getDocumentScanningError)).subscribe(errors => this.errors = errors));
  }

  browseFiles() {
    if (!this.fileSelectorElem) {
      return
    }

    this.fileSelectorElem.browseFiles().then(() => console.log('browse'));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBrowseFilesChange(event: any) {

    const files: FileList = (event as CustomEvent<{ files: FileList }>).detail.files;

    for (const file of Array.from(files)) {
      // Any validation you want to run once the file has been selected
      // can be done in here

      if (this.isValidFile(file)) {
        this.validFiles++;
      } else {
        this.invalidFiles++;
      }

      this.files.push(file);
    }
  }

  public async fileToBase64(file): Promise<DocScanWebComponentImageResult> {
    const fileReader = new FileReader();
    return new Promise((resolve) => {
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        const base64result = fileReader.result.toString().split(',')[1];
        resolve(new DocScanWebComponentImageResult(base64result, file.type));
      },
        reject => {
          fileReader.onerror = (err) => {
            reject(err);
          }
        };
    })
  }

  onFileRemove(i: number) {
    const file = this.files[i];
    if (this.isValidFile(file)) {
      this.validFiles--;
    } else {
      this.invalidFiles--;
    }
    this.files.splice(i, 1);
  }

  handleRemoveAllFiles() {
    this.files = []
    this.validFiles = 0;
    this.invalidFiles = 0;
  }

  async confirmAndUpload() {
    const base64Promises: Promise<DocScanWebComponentImageResult>[] = [];

    for (const file of Array.from(this.files)) {
      const base64Promise = this.fileToBase64(file);
      base64Promises.push(base64Promise);
    }

    Promise.all(base64Promises).then((images) => {
      const req = new DocScanWebComponentResult()
      req.images = images;
      this.sharedStore.dispatch(scanDocumentUsingWebService({
        formTemplateCode: this.formCode,
        docScanWebComponentResult: req,
        isBackoffice: this.isBackoffice
      }));
    });

  }

  skip() {
    this.sharedStore.dispatch(skipDocumentScanning())
  }

  private isValidFile(file: File) {
    const isValid = this.acceptedFileTypes.includes(file.type) && file.size <= 2000000;
    return isValid;
  }

  copyQrCodeValueToClipboard() {
    this.clipboard.copy(this.qrCodeValue);
  }

  retry() {
    this.sharedStore.dispatch(resetAutoFillForm());
  }
}
